import { tagged } from "@mirage/service-logging";

const logger = tagged("service-query-suggestions/circuit-breaker");

// This is a circuit breaker for the query-suggestions API
// It is used to prevent repeated API calls when we are receiving empty response
// or errors, and the cache cannot be hydrated.
export const RATE_LIMIT_CONFIG = {
  MAX_FAILURES: 3,
  CIRCUIT_BREAK_DURATION_MS: 30 * 60 * 1000, // 30 minute cool-down
};

const failureTrackers = new Map<
  string,
  {
    count: number;
    lastFailure: number;
  }
>();

export function recordSuccess(
  cacheKey: string,
  shouldBypassCircuitBreaker?: boolean,
) {
  if (shouldBypassCircuitBreaker) return;

  failureTrackers.delete(cacheKey);
}

export function recordFailure(
  cacheKey: string,
  shouldBypassCircuitBreaker?: boolean,
) {
  if (shouldBypassCircuitBreaker) return;

  const tracker = failureTrackers.get(cacheKey) || {
    count: 0,
    lastFailure: 0,
  };
  tracker.count += 1;
  tracker.lastFailure = Date.now();
  failureTrackers.set(cacheKey, tracker);
  logger.info("Recorded circuit breaker failure for cache key [hidden]", {
    failureCount: tracker.count,
    isCircuitBroken: tracker.count >= RATE_LIMIT_CONFIG.MAX_FAILURES,
  });
}

export function isCircuitBroken(
  cacheKey: string,
  shouldBypassCircuitBreaker?: boolean,
): boolean {
  if (shouldBypassCircuitBreaker) return false;

  const tracker = failureTrackers.get(cacheKey);
  if (!tracker) return false;

  const timeSinceLastFailure = Date.now() - tracker.lastFailure;
  if (timeSinceLastFailure > RATE_LIMIT_CONFIG.CIRCUIT_BREAK_DURATION_MS) {
    failureTrackers.delete(cacheKey);
    return false;
  }

  return tracker.count >= RATE_LIMIT_CONFIG.MAX_FAILURES;
}

export function circuitBreaker() {
  return {
    isCircuitBroken,
    recordFailure,
    recordSuccess,
    failureTrackers,
  };
}
